import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { makeSseParser } from "@/lib/sse";
import { stripInterrogatorTags } from "@/lib/stripTags";
import Message from "@/components/Message";
import ScoreBadge from "@/components/ScoreBadge";
import VoiceInput from "@/components/VoiceInput";
import TierOneRail from "@/components/TierOneRail";

const RING_LABELS = {
  1: "Concept & Viability",
  2: "Marketing & Financial Practicality",
  3: "Deep Trench Obstacles",
  4: "Growth Architecture",
};

// Map ring number to accent color for progress rail / active indicators
const RING_BAR_BG = { 1: "bg-emerald-500", 2: "bg-amber-500", 3: "bg-violet-500", 4: "bg-blue-800" };
const RING_TEXT = { 1: "ring-accent-1", 2: "ring-accent-2", 3: "ring-accent-3", 4: "ring-accent-4" };

const STYLE_LABELS = { blunt: "Blunt", coaching: "Coaching", socratic: "Socratic" };
const STYLE_ORDER = ["blunt", "coaching", "socratic"];

function VoiceStyleSwitcher({ current, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative mt-2 inline-block" data-testid="voice-style-switcher">
      <button
        type="button"
        className="mono-caps text-[0.6rem] px-2 py-1 rounded-md bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200 disabled:opacity-50"
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        data-testid="voice-style-switcher-btn"
      >
        Voice: {STYLE_LABELS[current] || current}
        <span className="ml-1 text-slate-400">▾</span>
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-40 bg-white border border-slate-200 rounded-md shadow-lg z-20 overflow-hidden">
          {STYLE_ORDER.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => { onChange(s); setOpen(false); }}
              className={`w-full text-left mono-caps text-[0.65rem] px-3 py-2 hover:bg-slate-50 ${
                s === current ? "bg-slate-50 font-semibold text-slate-900" : "text-slate-700"
              }`}
              data-testid={`voice-style-switch-${s}`}
            >
              {STYLE_LABELS[s]}
              {s === current && <span className="ml-2 text-emerald-600">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function IdeaChat() {
  const { id } = useParams();
  const nav = useNavigate();
  const [idea, setIdea] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState("");
  const [interim, setInterim] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [streaming, setStreaming] = useState(""); // live partial interrogator text
  const bottomRef = useRef(null);
  const loadedRef = useRef(false);
  const abortRef = useRef(null);

  useEffect(() => {
    // Guard against React.StrictMode double-invoke via one-shot ref.
    if (loadedRef.current) return;
    loadedRef.current = true;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/ideas/${id}`);
        setIdea(r.data.idea);
        setMessages(r.data.messages || []);
      } catch (e) {
        setError(e?.response?.data?.detail || "Could not load idea.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending, streaming]);

  useEffect(() => {
    if (idea && idea.status === "complete") {
      nav(`/ideas/${id}/verdict`, { replace: true });
    }
  }, [idea, id, nav]);

  async function send(e) {
    e.preventDefault();
    if (!input.trim() || sending) return;
    setSending(true);
    setError(null);
    setStreaming("");
    const text = input.trim();
    setInput("");
    setInterim("");
    const optimisticId = `tmp-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      { id: optimisticId, role: "user", content: text, ring: idea.current_ring, created_at: new Date().toISOString() },
    ]);

    const ac = new AbortController();
    abortRef.current = ac;
    const parse = makeSseParser();
    let doneFrame = null;

    try {
      const resp = await fetch(`${API}/ideas/${id}/message/stream`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: text }),
        signal: ac.signal,
      });
      if (!resp.ok) {
        let detail = "The interrogator did not respond.";
        try {
          const j = await resp.json();
          detail = j?.detail || detail;
        } catch { /* ignore */ }
        throw new Error(detail);
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let assembled = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunkText = decoder.decode(value, { stream: true });
        for (const frame of parse(chunkText)) {
          if (frame.event === "open") {
            // Server persisted the founder turn — swap the optimistic one.
            setMessages((prev) => prev.map((m) => (m.id === optimisticId ? frame.data.user_message : m)));
          } else if (frame.event === "delta") {
            assembled += frame.data.text || "";
            setStreaming(assembled);
          } else if (frame.event === "done") {
            doneFrame = frame.data;
          } else if (frame.event === "error") {
            throw new Error(frame.data.detail || "Interrogator errored.");
          }
        }
      }
      setStreaming("");
      if (doneFrame) {
        setIdea(doneFrame.idea);
        // Refetch messages so score badges and any newly-inserted ring-opener show up in order.
        const detail = await api.get(`/ideas/${id}`);
        setMessages(detail.data.messages || []);
        if (doneFrame.ring_completed || doneFrame.panel_triggered) {
          // RING-TIER-03 · Feature 2. Ring 3 clear routes to graduation
          // (not verdict) so the founder makes an explicit Tier 2 opt-in
          // decision. Ring 4 clear + panel run still routes to verdict.
          const ideaAfter = doneFrame.idea || {};
          const tier1 = (ideaAfter.tier_status || {}).tier1_complete;
          const tier2 = (ideaAfter.tier_status || {}).tier2_complete;
          if (tier1 && !tier2 && !doneFrame.panel_triggered) {
            nav(`/ideas/${id}/graduation`);
          } else {
            nav(`/ideas/${id}/verdict`);
          }
        }
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        setError(err?.message || "The interrogator did not respond. Try again.");
        setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
      }
      setStreaming("");
    } finally {
      setSending(false);
      abortRef.current = null;
    }
  }

  if (loading) {
    return <div className="min-h-screen px-8 py-14 text-slate-500">Loading transcript…</div>;
  }
  if (!idea) {
    return (
      <div className="min-h-screen px-8 py-14">
        <div className="text-red-600" data-testid="idea-load-error">{error || "Idea not found."}</div>
        <button className="btn-ghost mt-6" onClick={() => nav("/dashboard")}>← Back</button>
      </div>
    );
  }

  const currentRing = idea.current_ring;
  const ringMessages = messages.filter((m) => m.ring === currentRing);
  const priorRings = [1, 2, 3, 4].filter((r) => r < currentRing);
  const disabled = idea.status === "no_go" || idea.status === "complete";

  // Ring 4 progress marker: pull the highest [Q: N/12] from any Ring 4
  // assistant message (including the currently-streaming partial). Server
  // instructs the model to emit it verbatim at the end of each Ring 4 turn.
  const ring4QuestionNumber = (() => {
    if (currentRing !== 4) return null;
    let maxN = null;
    const scan = (t) => {
      const matches = (t || "").matchAll(/\[Q:\s*(\d+)\/12\]/gi);
      for (const m of matches) {
        const n = parseInt(m[1], 10);
        if (!isNaN(n) && (maxN == null || n > maxN)) maxN = n;
      }
    };
    for (const m of messages) if (m.ring === 4 && m.role === "assistant") scan(m.content);
    if (streaming) scan(streaming);
    return maxN;
  })();

  return (
    <div className="min-h-screen">
      <div className="max-w-[1120px] mx-auto px-8 py-10">
        {/* RING-TIER-03 · Feature 3 — persistent Tier 1 rail (Rings 1–3 only) */}
        {currentRing <= 3 && <TierOneRail idea={idea} ideaId={id} />}
        {/* Header */}
        <div className="flex items-start justify-between gap-6 mb-8">
          <div className="min-w-0">
            <button
              className="mono-caps hover:text-slate-900 transition-colors mb-4"
              onClick={() => nav("/dashboard")}
              data-testid="chat-back-to-dashboard"
            >
              ← Dashboard
            </button>
            <h1 className="font-display text-3xl sm:text-4xl leading-tight truncate">{idea.title}</h1>
          </div>
          <div className="text-right shrink-0">
            <div className={`mono-caps mb-2 ${RING_TEXT[currentRing]}`} data-testid="current-ring-indicator">
              Ring {currentRing} / 4
            </div>
            <div className="mono-caps text-slate-400 text-[0.65rem]">
              {RING_LABELS[currentRing]}
            </div>
            {idea.voice_style && (
              <VoiceStyleSwitcher
                current={idea.voice_style}
                onChange={async (next) => {
                  if (next === idea.voice_style) return;
                  try {
                    const r = await api.patch(`/ideas/${id}/voice-style`, { voice_style: next });
                    setIdea(r.data.idea);
                    if (r.data.signature) {
                      toast(r.data.signature, {
                        duration: 4000,
                        // Style the toast so it FEELS like the new persona speaking
                        className: `voice-toast voice-toast-${next}`,
                      });
                    }
                  } catch (e) {
                    setError(e?.response?.data?.detail || "Could not change voice style.");
                  }
                }}
                disabled={disabled}
              />
            )}
            {currentRing === 4 && ring4QuestionNumber != null && (
              <div
                className="inline-block mono-caps text-[0.65rem] px-2.5 py-1 rounded-md mt-2 bg-blue-50 text-blue-800 border border-blue-200"
                data-testid="ring4-progress-pill"
              >
                Question {ring4QuestionNumber} of 12
              </div>
            )}
          </div>
        </div>

        {/* Ring 3 unlock banner — persistent CTA to briefs + executive dashboard.
            Renders the moment current_ring passes 3 (i.e. Ring 3 is cleared),
            through the rest of the Gauntlet including complete/verdict states. */}
        {currentRing >= 4 && (
          <div
            className="mb-8 rounded-lg border border-emerald-200 bg-gradient-to-r from-emerald-50 to-white px-5 py-4 flex flex-wrap items-center justify-between gap-3"
            data-testid="ring3-unlock-banner"
          >
            <div className="min-w-0">
              <div className="mono-caps text-[0.6rem] text-emerald-700 mb-1">
                Ring 3 cleared · Package unlocked
              </div>
              <div className="text-slate-700 text-sm">
                Your Business Brief, Full-Depth Brief, Checklist Scorecard, and Executive Dashboard are ready.
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button
                className="mono-caps text-[0.65rem] px-3 py-1.5 rounded border border-emerald-300 text-emerald-800 hover:bg-emerald-100"
                onClick={() => nav(`/ideas/${id}/dashboard`)}
                data-testid="ring3-open-dashboard"
              >
                Executive Dashboard →
              </button>
              <button
                className="mono-caps text-[0.65rem] px-3 py-1.5 rounded border border-slate-300 text-slate-700 hover:bg-slate-50"
                onClick={() => nav(`/ideas/${id}/documents`)}
                data-testid="ring3-open-docs"
              >
                Download briefs →
              </button>
            </div>
          </div>
        )}

        {/* Dial-in mode strip — shown when the founder has reopened a prior ring.
            Live indicator so they don't lose track that new turns are patching
            a prior ring's score, not advancing them from Ring 4. */}
        {idea.active_ring && idea.active_ring < currentRing && (
          <div
            className="mb-8 rounded-lg border border-amber-300 bg-amber-50 px-5 py-3 flex items-center justify-between gap-3"
            data-testid="dial-in-strip"
          >
            <div className="min-w-0">
              <span className="mono-caps text-[0.6rem] text-amber-800 mr-2">Dial-in mode</span>
              <span className="text-amber-900 text-sm">
                You're improving Ring {idea.active_ring}. New turns update its score
                without touching your Ring 4 progress.
              </span>
            </div>
            <button
              className="mono-caps text-[0.65rem] px-3 py-1.5 rounded border border-amber-500 text-amber-900 hover:bg-amber-100"
              onClick={async () => {
                try {
                  const r = await api.post(`/ideas/${id}/resume-frontier`);
                  setIdea(r.data.idea);
                  toast.success("Back at the frontier");
                } catch (e) {
                  setError(e?.response?.data?.detail || "Could not resume.");
                }
              }}
              data-testid="dial-in-resume"
            >
              Resume Ring {currentRing} →
            </button>
          </div>
        )}
        <div className="flex gap-4 mb-12 items-center" data-testid="ring-progress-rail">
          {[1, 2, 3, 4].map((r) => {
            const score = idea[`ring${r}_score`];
            const cleared = score?.advance_ready && score?.score >= 65 && currentRing > r;
            const active = currentRing === r;
            const locked = currentRing < r;
            const barColor = cleared || active ? RING_BAR_BG[r] : "bg-slate-200";
            return (
              <div key={r} className="flex-1" data-testid={`ring-progress-${r}`}>
                <div className={`h-[3px] rounded-full ${barColor} ${active && !cleared ? "opacity-60" : ""}`} />
                <div className="mt-2 flex items-center justify-between">
                  <span className={`mono-caps ${active ? RING_TEXT[r] : locked ? "text-slate-300" : "text-slate-500"}`}>
                    Ring {r}
                  </span>
                  {score && <span className="mono-caps text-slate-400">{score.score}/100</span>}
                </div>
              </div>
            );
          })}
        </div>

        {/* Advance banner */}
        {currentRing > 1 && idea[`ring${currentRing - 1}_score`]?.advance_ready && (
          <div className="mb-10 rounded-xl border border-blue-200 bg-blue-50 px-5 py-4">
            <div className="mono-caps text-blue-800 mb-1" data-testid="advance-confirmation">
              Ring {currentRing - 1} cleared · Ring {currentRing} open
            </div>
            <div className="text-slate-700 text-sm">
              Score: {idea[`ring${currentRing - 1}_score`].score}/100 · signal: {idea[`ring${currentRing - 1}_score`].signal}
            </div>
          </div>
        )}

        {/* Prior rings collapsed */}
        {priorRings.map((r) => {
          const rMsgs = messages.filter((m) => m.ring === r);
          if (rMsgs.length === 0) return null;
          return (
            <details key={r} className="mb-6 border border-slate-200 rounded-xl overflow-hidden" data-testid={`prior-ring-${r}`}>
              <summary className={`cursor-pointer px-5 py-4 mono-caps hover:bg-slate-50 flex justify-between items-center ${RING_TEXT[r]}`}>
                <span>Ring {r} transcript · {rMsgs.length} exchanges</span>
                {idea[`ring${r}_score`] && <ScoreBadge score={idea[`ring${r}_score`]} />}
              </summary>
              <div className="px-5 py-6 space-y-8 border-t border-slate-200 bg-white">
                {rMsgs.map((m) => (
                  <Message
                    key={m.id}
                    message={m}
                    ideaId={idea.id}
                    flaggedFigures={idea.unsupported_figures || []}
                  />
                ))}
              </div>
            </details>
          );
        })}

        {/* Current ring transcript */}
        <div className="space-y-10" data-testid="chat-transcript">
          {ringMessages.map((m) => (
            <Message
              key={m.id}
              message={m}
              ideaId={idea.id}
              flaggedFigures={idea.unsupported_figures || []}
            />
          ))}
          {streaming && (
            <div className="chat-message assistant" data-testid="chat-streaming-turn">
              <div className="mono-caps mb-2 text-slate-500">
                Interrogator · Ring {currentRing}
              </div>
              <div className="text-slate-800 whitespace-pre-wrap leading-relaxed">
                {stripInterrogatorTags(streaming)}
                <span
                  className="inline-block w-2 h-4 ml-1 align-middle animate-pulse bg-slate-800"
                />
              </div>
            </div>
          )}
          {sending && !streaming && (
            <div className="mono-caps text-slate-400 animate-pulse" data-testid="chat-thinking">
              Interrogator thinking…
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Composer */}
        {!disabled && (
          <form onSubmit={send} className="mt-14 pt-10 border-t border-slate-200">
            {error && (
              <div className="text-red-600 text-sm font-medium mb-3" data-testid="chat-error">
                {error}
              </div>
            )}
            <label className="mono-caps block mb-3">Your response</label>
            <textarea
              value={input + (interim ? ` ${interim}` : "")}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Answer with specifics — type or click the mic to speak. Vagueness will be named."
              className="input-line"
              rows={5}
              disabled={sending}
              data-testid="chat-input"
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                  send(e);
                }
              }}
            />
            <div className="mt-5 flex items-center gap-4 flex-wrap">
              <button
                type="submit"
                className="btn-primary"
                disabled={sending || !input.trim()}
                data-testid="chat-send"
              >
                {sending ? "Sending…" : "Send response"}
              </button>
              <VoiceInput
                onTranscript={(chunk) => {
                  setInput((prev) => (prev.trim() ? `${prev} ${chunk}` : chunk));
                  setInterim("");
                }}
                onInterim={setInterim}
                disabled={sending}
                size="md"
              />
              <span className="mono-caps text-slate-400 text-[0.65rem]">⌘ + ↵ to send</span>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
